import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import { Get, Post } from "@repo/utils/api";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import { api } from "..";

import { Customer, useCustomers } from "./customers";
import { EmailTemplate, useEmailTemplates } from "./emailTemplates";
import { Organization } from "./organizations";

export type EmailSend = {
  id: EmailSend.Id;
  name: string;
  emailTemplateId: EmailTemplate.Id;
  emailTemplateVersionId: EmailTemplate.Version.Id;
  customerGroupId: Customer.Group.Id;
  status: "pending" | "sent" | "failed";
  subjectLine: string;
  sendAt?: Date;
  createdAt: Date;
  sendCounts: {
    total: number;
    opened: number;
    bounced: number;
  };
};

export namespace EmailSend {
  export type Id = `ebs_${string}`;
  export type Params = Pick<
    EmailSend,
    "name" | "emailTemplateVersionId" | "subjectLine" | "sendAt"
  > & {
    customerGroupId: Customer.Group.Query.Id;
    dynamicParams: Record<string, string>;
  };
}

export type EmailSendsRest = {
  [id: EmailSend.Id]: Get<
    {},
    {
      emailSend: EmailSend;
    }
  >;
};
export namespace EmailSendsRest {
  export type Organization = Get<
    {},
    {
      emailSends: EmailSend[];
      customerGroups: Customer.Group[];
      customerGroupQueries: Customer.Group.Query[];
      emailTemplates: EmailTemplate[];
      emailTemplateVersions: EmailTemplate.Version[];
    }
  > &
    Post<EmailSend.Params, { emailSend: EmailSend }>;
}

const initialState = {
  byId: {} as Record<EmailSend.Id, EmailSend>,
  forOrganization: {} as Record<Organization.Id, EmailSend.Id[]>,
};

export const { Context, Provider, useEmailSends } = FeatureStore.init(
  "EmailSends",
  initialState,
  ([store, setStore], { withLogger, attachAction }) => {
    const [, setCustomers] = useCustomers();
    const [, setEmailTemplates] = useEmailTemplates();

    const accessors = {
      byId: attachAction(
        (id: EmailSend.Id) => store.byId[id],
        (id) => actions.fetch(id),
      ),
      forOrganization: attachAction(
        (orgId: Organization.Id) =>
          store.forOrganization[orgId]?.map((id) => store.byId[id]!),
        (orgId) => actions.fetchFor(orgId),
      ),
    };

    const mutations = {
      one(emailSend: EmailSend) {
        setStore("byId", emailSend.id, reconcile(emailSend));
      },
      many(emailSends: EmailSend[]) {
        for (const emailSend of emailSends) {
          mutations.one(emailSend);
        }
      },
    };

    const actions = withLogger("action", {
      fetch(id: EmailSend.Id) {
        return api["email-sends"][id]!.GET({}).then(({ emailSend }) => {
          mutations.one(emailSend);
        });
      },
      fetchFor(orgId: Organization.Id) {
        return api.organizations[orgId]!["email-sends"].GET({}).then(
          ({
            emailSends,
            customerGroupQueries,
            customerGroups,
            emailTemplateVersions,
            emailTemplates,
          }) => {
            batch(() => {
              setCustomers.groups.many(customerGroups);
              setCustomers.groups.manyQueries(customerGroupQueries);
              setEmailTemplates.many(emailTemplates);
              setEmailTemplates.manyVersions(emailTemplateVersions);
              mutations.many(emailSends);
              setStore(
                "forOrganization",
                orgId,
                emailSends.map(({ id }) => id),
              );
            });
          },
        );
      },
      create(orgId: Organization.Id, params: EmailSend.Params) {
        return api.organizations[orgId]!["email-sends"].POST(params).then(
          ({ emailSend }) => {
            batch(() => {
              mutations.one(emailSend);
              setStore("forOrganization", orgId, (ids = []) => [
                ...ids,
                emailSend.id,
              ]);
            });
          },
        );
      },
    });

    return { accessors, actions };
  },
);
